import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Typography } from '@healingpaper/cell'

import Button from '@/components/cell/molecules/Button'
import { EventList } from '@/components/pages/eventList'
import { useScroll } from '@/hooks/Scroll'
import { MoreInfo } from '@/components'
import { ServiceOfferCard } from '@/features/service-offer/components/service-offer-card'
import { generateTagId } from '@/utils/test-automation'

import type { EventListItemOnClickEventParam } from '@/components/pages/eventList/EventListItem'
import type { SearchServiceOffer, SearchServiceOfferItem } from '@/features/search/api/search.types'

export interface Props {
  serviceOffers: SearchServiceOfferItem[]
  eventTotalLength: number
  queryKeyword?: string
  districtName?: string
  lastIndex: number
  discountedCostText: string
  isMoreData: boolean
  onClickEvent: (event: EventListItemOnClickEventParam, index: number) => void
  onReachBottom: () => void
  onClickMoreButton: () => void
  onImpressedEvent?: (event: SearchServiceOffer, index: number) => void
}

const MARGIN_REACH_BOTTOM = 150

const EventListTemplate = ({
  eventTotalLength,
  queryKeyword,
  districtName,
  serviceOffers,
  isMoreData,
  onClickEvent,
  onReachBottom,
  onClickMoreButton,
  onImpressedEvent,
}: Props): JSX.Element => {
  const { t } = useTranslation()
  const { initScroll, subscribe, unsubscribe } = useScroll()
  const [isImpressionVisible, setIsImpressionVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)

  const text = useMemo(() => {
    const keywordTexts = []
    if (queryKeyword) {
      keywordTexts.push(queryKeyword)
    }
    if (districtName) {
      keywordTexts.push(districtName)
    }

    return {
      moreButton: t('components.moreButton.contents.text'),
      queryResultTitle: t('event.list.queryResult', { keyword: keywordTexts.join(' ') }),
      unit: t('common.unit.length'),
      vat: t('event.vat'),
      certifiedHospital: t('hospital.certifiedHospital'),
      specialPrice: t('common.price.specialPrice'),
    }
  }, [queryKeyword, districtName, t])

  useEffect(() => {
    initScroll()
    setIsLoading(false)

    const onScroll = (e: { scrollY: number }) => {
      const body = document.documentElement
      if (
        body &&
        bottomRef.current &&
        e.scrollY + body.clientHeight - MARGIN_REACH_BOTTOM > bottomRef.current.offsetTop
      ) {
        setIsLoading(true)
        onReachBottom()
      }
    }

    if (localStorage && localStorage.getItem('isImpressionVisible') === 'true') {
      setIsImpressionVisible(true)
    }

    subscribe('scroll', onScroll)
    return () => {
      unsubscribe('scroll', onScroll)
    }
  }, [serviceOffers])
  return (
    <div className="flex flex-col pt-4">
      {(queryKeyword || districtName) && (
        <div className="px-5 pb-2">
          <Typography type="title3" elementType="h1">
            {text.queryResultTitle}
            <Typography type="title3" color="orange" elementType="span">
              {` ${eventTotalLength.toLocaleString()}`}
            </Typography>
            {text.unit}
          </Typography>
        </div>
      )}
      <EventList length={isLoading ? -1 : serviceOffers.length}>
        <div className="flex flex-col gap-[1px] bg-[#ECECEC]">
          {serviceOffers.map((event: SearchServiceOfferItem, index: number) => {
            const serviceOffer = event.serviceOffer
            if (!serviceOffer) {
              return null
            }

            const {
              id,
              hospital: { id: hospitalId, district: hospitalSido, name: hospitalName },
              thumbnail,
              rating: { amount: ratingAmount, count: ratingCount },
              price: { originalAmount, discountedAmount, currency },
              representativeOption: {
                id: optionId,
                price: {
                  originalAmount: representativeOriginalAmount,
                  discountedAmount: representativeDiscountedAmount,
                  currency: representativeCurrency,
                  exchangeRateAppliedPrice,
                },
              },
              title,
            } = serviceOffer
            const finalDiscountedAmount =
              exchangeRateAppliedPrice?.discountedAmount || representativeDiscountedAmount || discountedAmount
            const finalOriginalAmount =
              exchangeRateAppliedPrice?.originalAmount || representativeOriginalAmount || originalAmount
            const finalCurrency = exchangeRateAppliedPrice?.currency || representativeCurrency || currency

            return (
              <ServiceOfferCard
                elementId={generateTagId('event', 'card', 'component', 'ui', index.toString())}
                key={`${id}_${index}_${queryKeyword || 'none'}`}
                eventId={id}
                thumbnailUrl={thumbnail}
                title={title}
                subtitle={`${hospitalSido}${hospitalName ? `・${hospitalName}` : ''}`}
                review={{
                  rating: ratingAmount,
                  count: ratingCount,
                }}
                price={{
                  originalCost: finalOriginalAmount,
                  discountedCost: finalDiscountedAmount,
                  currency: finalCurrency,
                }}
                optionId={optionId || ''}
                titleElementType="h2"
                priceElementType="h3"
                trackClickEvent={() =>
                  onClickEvent(
                    {
                      id,
                      title,
                      hospitalId,
                      discountedCost: representativeDiscountedAmount,
                      cost: representativeOriginalAmount,
                      currency: representativeCurrency,
                      discountRate: 0,
                    },
                    index,
                  )
                }
                onImpressed={() => {
                  if (serviceOffer) {
                    onImpressedEvent?.(serviceOffer, index)
                  }
                }}
              />
            )
          })}
        </div>
      </EventList>

      {/* 인피니티 로딩을 위한 스레시홀드 */}
      {serviceOffers.length > 20 && <div ref={bottomRef} />}

      {isMoreData && serviceOffers.length <= 20 && (
        <div className="p-4">
          <Button className="w-full" color="primary" size="large" onClick={onClickMoreButton}>
            {text.moreButton}
          </Button>
        </div>
      )}

      {isMoreData && <MoreInfo isShow={isLoading} />}
    </div>
  )
}

export default EventListTemplate
